import { Avatar, Box, Card, CircularProgress, Divider, IconButton, Stack, useTheme } from "@mui/material";
import React, { useMemo, useState } from "react";
import { assetService, cacheService, thumbnailService } from "../../Core/Api/services";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ModeEditOutlinedIcon from "@mui/icons-material/ModeEditOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { ModalContext } from "../../Components/Modals";
import ModalEditArtworkMetadata, {
  EditArtworkMetadataProps,
} from "../../Components/Modals/ModalEditArtworkMetadata/modal-edit-artwork-metadata";
import ModalConfirmAction, {
  ModalConfirmActionElementProps,
} from "../../Components/Modals/ModalConfirmAction/modal-confirm-action";
import { useDispatch } from "react-redux";
import { HandleChangeArtworkMetadata, RemoveArtwork } from "../../Redux/Reducers/redux-slice-bundle-maker";
import useCustomSnackbar from "../../Utils/Hooks/useCustomSnackbar";
import BrokenImageIcon from "@mui/icons-material/BrokenImage";
import { Img } from "react-image";
import { IsPermissionGranted } from "../../Core/Utils/asset-object-utils";
import { PermissionType } from "../../Core/Api/Types/permission-type";
import { useTranslation } from "react-i18next";

type artworkCardProps = {
  data: any;
  index: number;
  setPosterViewerVisible;
  imgWidth: number;
};

const ArtworkCard: React.FC<artworkCardProps> = (props: artworkCardProps) => {
  const { t } = useTranslation();
  const { data, index, setPosterViewerVisible, imgWidth } = props;
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };
  const [showActions, setShowActions] = useState(false);
  const dispatch = useDispatch();

  const enqueueCustomSnackbar = useCustomSnackbar();

  const theme = useTheme();

  const onClose: EditArtworkMetadataProps["onClose"] = (data) => {
    enqueueCustomSnackbar(t("alert:success.artworkMetadataUpdated"), "success");
    dispatch(HandleChangeArtworkMetadata({ artworkId: data.artworkId, metadata: data.metadata }));
  };

  const handleEditArtwork = (e) => {
    e.stopPropagation();
    openModal &&
      openModal<EditArtworkMetadataProps>(ModalEditArtworkMetadata, {
        data,
        onClose,
      });
  };
  const handleDeleteArtwork = (e, id: number) => {
    e.stopPropagation();
    if (!openModal) return;

    openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
      displayComment: false,
      title: t("modal:title.deleteArtwork"),
      onConfirm: () => {
        assetService.DeleteObjectById(id).then((res) => {
          if (res.success) {
            enqueueCustomSnackbar(t("alert:success.artworkDeleted"), "success");
            dispatch(RemoveArtwork(id));
          } else {
            enqueueCustomSnackbar(res.message || t("alert:error.deletingArtwork"), "error");
          }
        });
      },
    });
  };

  const srcUrl = useMemo(
    () => `${thumbnailService.GetServiceUrl()}/thumbs/reference/${cacheService.GetAuthKey()}/${data.id}`,
    [data]
  );
  const imgRatio = useMemo(
    () =>
      data.metadata.find((x) => cacheService.GetMetadata().find((m) => m.tagName === "imageAspectRatio")?.id === x.id)
        ?.value || 1,
    [data]
  );

  const userHasRightToDelete = useMemo(() => IsPermissionGranted(data, PermissionType.Trash), [data]);

  const _renderAvatar = (loading: boolean) => (
    <Avatar
      variant="square"
      sx={{
        width: imgWidth,
        height: imgWidth / imgRatio,
        color: "text.primary",
        bgcolor: "backgroundAlpha.main",
      }}
    >
      {loading ? <CircularProgress /> : <BrokenImageIcon />}
    </Avatar>
  );

  return (
    <>
      {data && data.id && (
        <Card
          onMouseEnter={() => setShowActions(true)}
          onMouseLeave={() => setShowActions(false)}
          onClick={() => setPosterViewerVisible(data.id)}
          sx={{
            border: "0px",
            cursor: showActions ? "pointer" : "auto",
            width: imgWidth,
            height: imgWidth / imgRatio,
            position: "relative",
            margin: `${imgWidth / 10}px`,
            transition: "0.2s, transform 0.4s",
            transform: showActions ? "scale(1.05)" : "scale(1)",
            boxShadow: showActions ? "0px 0px 4px 4px rgba(95, 95, 95, 0.5)" : "initial",
          }}
          key={index}
        >
          <Img
            src={srcUrl}
            loader={_renderAvatar(true)} // Used while loading
            unloader={_renderAvatar(false)}
          />

          {showActions && (
            <Stack
              sx={{
                position: "absolute",
                zIndex: 10,
                bottom: 0,
                right: 0,
                left: imgWidth > 149 ? "auto" : 0,
                borderRadius: "5px",
                display: "flex",
                flexDirection: "row",
                flexWrap: "nowrap",
                justifyContent: imgWidth > 149 ? "flex-end" : "space-around",
                height: `${imgWidth / 10 + 20}px`,
                opacity: 0.8,
                margin: `${imgWidth / 38}px`,
              }}
            >
              <IconButton
                sx={{
                  backgroundColor: `${theme.palette.grey[500]}`,
                  marginLeft: imgWidth > 149 ? "3px" : "0px",
                  marginRight: imgWidth > 149 ? "3px" : "0px",
                  padding: `${imgWidth / 15}px ${imgWidth / 25}px ${imgWidth / 15}px ${imgWidth / 25}px`,
                }}
                onClick={(e) => handleEditArtwork(e)}
              >
                <ModeEditOutlinedIcon sx={{ width: `${10 + imgWidth / 15}px`, height: `${10 + imgWidth / 15}px` }} />
              </IconButton>
              <Divider flexItem orientation="vertical" />
              <IconButton
                onClick={(e) => handleDeleteArtwork(e, data.id)}
                sx={{
                  backgroundColor: `${theme.palette.grey[500]}`,
                  marginLeft: imgWidth > 149 ? "3px" : "0px",
                  marginRight: imgWidth > 149 ? "3px" : "0px",
                  padding: `${imgWidth / 15}px ${imgWidth / 25}px ${imgWidth / 15}px ${imgWidth / 25}px`,
                }}
                disabled={!userHasRightToDelete}
              >
                <DeleteOutlineIcon sx={{ width: `${10 + imgWidth / 15}px`, height: `${10 + imgWidth / 15}px` }} />
              </IconButton>
            </Stack>
          )}
          <Box
            sx={{
              position: "absolute",
              display: "flex",
              justifyContent: "center",
              alignContent: "center",
              alignItems: "center",
              width: "100%",
              height: "100%",
              opacity: showActions ? 0.25 : 0,
              transitionProperty: "opacity",
              transitionDuration: "0.2s",
              top: 0,
            }}
          >
            <VisibilityOutlinedIcon
              sx={{
                width: "50%",
                height: "50%",
              }}
            />
          </Box>
        </Card>
      )}
    </>
  );
};

export default ArtworkCard;
